import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, ExternalLink, Info } from "lucide-react";
import { BYOK_PROVIDERS, PREMIUM_RECOMMENDED } from "@/data/byokProviders";

/**
 * ByokSetupChecklist
 * --------------------------------------------------------------
 * Compact in-app onboarding strip for the BYOK page. Shows the
 * 4 recommended Premium providers and a green-tick / amber-dot
 * state next to each so a fresh Premium user immediately sees
 * "I still need OpenAI + Anthropic" without scrolling through
 * the full provider grid. Also explicit about Sora's proxy
 * limitation so the founder isn't surprised that "video bills
 * fall on the Universal Key even with my OpenAI key saved."
 */
export default function ByokSetupChecklist({ keys = [] }) {
  const savedSet = new Set(keys.map((k) => k.provider));
  const recommended = BYOK_PROVIDERS.filter((p) => PREMIUM_RECOMMENDED.includes(p.id));
  const savedCount = recommended.filter((p) => savedSet.has(p.id)).length;

  return (
    <div className="mb-8 border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A0A0A] to-[#121212] p-6 sparkle-bed" data-testid="byok-checklist">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">Premium Setup Checklist</p>
          <h3 className="font-display text-2xl font-extrabold gold-shimmer">What keys do I need?</h3>
          <p className="text-sm text-[#A0A0A0] mt-2 max-w-2xl">
            Connect any of the providers below to run generations on your own billing. You don't need all four —
            most creators start with <span className="text-[#F4F4F0]">Gemini</span> (image) and{" "}
            <span className="text-[#F4F4F0]">Anthropic</span> (writing) for the strongest combo.
          </p>
        </div>
        <div className="hidden sm:block text-right shrink-0">
          <div className="font-display text-3xl font-black gold-shimmer">{savedCount}<span className="text-[#5A5A5A]">/{recommended.length}</span></div>
          <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mt-1">Recommended saved</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="byok-checklist-grid">
        {recommended.map((p) => {
          const saved = savedSet.has(p.id);
          const Icon = p.icon;
          return (
            <div
              key={p.id}
              data-testid={`byok-checklist-${p.id}`}
              className={`flex items-start gap-3 p-3.5 border transition-colors ${
                saved
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-[#2A2A2A] bg-[#0A0A0A] gold-glow"
              }`}
            >
              <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${p.accent}`} strokeWidth={1.7} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-semibold text-sm text-[#F4F4F0]">{p.name}</span>
                  {saved ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300">
                      <AlertCircle className="w-3 h-3" /> Add this
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#A0A0A0] leading-relaxed">{p.summary}</p>
                {!saved && (
                  <a
                    href={p.where}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`byok-where-${p.id}`}
                    className="inline-flex items-center gap-1 mt-2 text-[10px] uppercase tracking-wider text-[#D4AF37] hover:text-[#F0C84B] transition-colors"
                  >
                    Where to get this <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sora-via-Emergent transparency callout — owners specifically asked for
          this. Without it, an owner who saves their OpenAI key and tries video
          gen sees "fallback used → Universal Key" and assumes BYOK is broken.
          It isn't — it's a provider limitation. */}
      <div className="mt-5 flex items-start gap-3 p-3.5 border border-amber-500/30 bg-amber-500/5 text-amber-200" data-testid="byok-sora-callout">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="text-xs leading-relaxed">
          <span className="font-semibold">Note on Sora 2 video:</span> Even with your OpenAI key connected,
          Sora 2 video calls still proxy through Emergent's gateway today because OpenAI hasn't opened
          public Sora API access yet. <span className="text-amber-100">Image, voice, ad scripts and writing all use your keys directly.</span>{" "}
          The moment OpenAI opens partner Sora access, video flips onto your key automatically — no code change on your side.
        </div>
      </div>

      <p className="mt-4 text-[10px] uppercase tracking-wider text-[#5A5A5A] flex items-center gap-2">
        <span>Need the full provider list?</span>
        <Link to="#provider-grid" className="text-[#D4AF37] hover:text-[#F0C84B] transition-colors">Scroll to the provider grid below ↓</Link>
      </p>
    </div>
  );
}
